// CROSS-SYSTEM FIELD RECENCY — the silent arbiter behind "the table's edit wins,
// then D&D Beyond reasserts". (Finishing #54 per docs/RECENCY-AUTHORITY-ANALYSIS.md §5.)
//
// A PC's combat fields (HP, temp HP, conditions, exhaustion, death saves) are
// co-authored by THREE systems at once: D&D Beyond (the ~30 s live poll), Roll20
// (a bar edited at the table) and the DM Screen itself (a quick action). With no
// arbiter, each inbound apply blindly overwrote the others — so a fresh Roll20 HP
// edit got reverted by the next stale D&D Beyond poll (the bug Trevor hit).
//
// WHY NOT CLOCK TIMESTAMPS. DDB's dateModified is on the DDB server clock; a
// Roll20/DMS edit is stamped on the local clock. Comparing across the two clocks
// silently mis-orders (the §5 red-team killed the calibration idea — the DDB write
// here is DOM automation with no response Date, and the ~30 s poll means a DDB
// edit is *seen* long after a racing table edit already applied, so it cannot be
// time-ordered against it at all). So we DO NOT cross-clock compare.
//
// THE MECHANISM — an OVERRIDE-HOLD, mirroring the sheet-override pattern (local
// beats sync until reset):
//   * A table edit (Roll20 or DMS) to a field OWNS it for a bounded hold. While the
//     hold is live, an inbound DDB poll for that field LOSES — whatever its
//     timestamp, present or absent. Skew-immune, and it fixes the two v1 holes:
//     a no-timestamp DDB event can no longer blind-win (Slice 1), and the DM's own
//     edit is protected once the caller holds it (Slice 1/3).
//   * The hold ends on a confirmed DDB write-back (releaseHolds, the echo of our own
//     write) or after holdMs — then DDB REASSERTS (canonical catches up).
//   * DDB-vs-DDB is ordered by dateModified alone — monotonic per character, one
//     clock, no hazard.
// The HP triad (hp / maxHp / tempHp) is arbitrated as ONE coupled unit (Slice 3):
// all three fold to a single key, so a verdict can never apply maxHp while dropping
// hp and break the hp ≤ maxHp invariant, and whichever ORIGIN last touched any of
// them owns all three (route-by-origin, not static field name).
//
// It is SILENT: callers drop a losing field, never prompt the DM mid-fight. Scope
// is cross-SYSTEM on the DM Screen (the hub); cross-WINDOW Compass writes remain a
// known limit, out of scope here.

export type RecencySource = "ddb" | "roll20" | "dms";
/** A table source authors "at the table" and so takes a hold; DDB is canonical and reasserts. */
const isTableSource = (s: RecencySource): boolean => s === "roll20" || s === "dms";

/** The co-authored combat fields, by Character field name, so a caller can pass
 *  Object.keys(change) directly. AC / saves are D&D Beyond-computed (local
 *  overrides) and deliberately NOT arbitrated here. */
export const RECENCY_FIELDS = ["hp", "maxHp", "tempHp", "conditions", "exhaustion", "deathSuccesses", "deathFailures"] as const;
export type RecencyField = (typeof RECENCY_FIELDS)[number];

/* THE HP TRIAD IS ONE UNIT (§5). hp, maxHp and tempHp move together — a table drain
   lowers maxHp (and clamps hp), a DDB feature grants tempHp — so arbitrating them
   per-field could apply one and drop another and break hp ≤ maxHp. They fold to a
   single canonical key, so one verdict governs the whole triad and the ORIGIN that
   last touched any of them owns all three. maxHp is thereby arbitrated too, even
   though on its own it reads as "DDB-computed": the origin, not the field name,
   decides (route-by-origin). */
const TRIAD = new Set(["hp", "maxHp", "tempHp"]);
const canon = (field: string): string => (TRIAD.has(field) ? "hp" : field);

/** Canonical keys that are arbitrated. The triad collapses to "hp". */
const TRACKED = new Set<string>(["hp", "conditions", "exhaustion", "deathSuccesses", "deathFailures"]);
const isArbitrated = (field: string): boolean => TRACKED.has(canon(field));

/** How long a table edit owns a field before DDB may reassert, absent a confirmed
 *  write-back first. A floor to survive the ~30 s DDB poll lag plus the write round
 *  trip; tuned at a live table (Slice 4). */
export const DEFAULT_HOLD_MS = 45000;

/** Kept as the SETTABLE override the design calls for (§3.4), not the default path:
 *  the override-hold decides the common case, so most DMs never set this. */
export const DEFAULT_PC_PRIORITY: RecencySource[] = ["ddb", "roll20", "dms"];

interface Stamp { at: number; source: RecencySource }
interface Hold { until: number; source: RecencySource }
// id -> canonical-field -> stamp / hold. Module-level, per session (cleared on reload).
const stamps = new Map<string, Map<string, Stamp>>();
const holds = new Map<string, Map<string, Hold>>();

export interface RecencyOpts { now?: number; holdMs?: number }

/** The source of a live hold on (id, field), or null if none is active. Lazily
 *  drops an expired hold so DDB reasserts cleanly the next time it is asked. */
export function activeHold(id: string, field: string, now: number = Date.now()): RecencySource | null {
  const m = holds.get(id);
  const h = m?.get(canon(field));
  if (!h) return null;
  if (now >= h.until) { m!.delete(canon(field)); return null; }
  return h.source;
}

/** A table edit takes (or refreshes) the hold on these fields. DDB never holds —
 *  it is the canonical source that reasserts, so a `ddb` call here is a no-op. */
export function holdFields(id: string, fields: Iterable<string>, source: RecencySource, now: number = Date.now(), holdMs: number = DEFAULT_HOLD_MS): void {
  if (!isTableSource(source)) return;
  let m = holds.get(id);
  for (const field of fields) {
    if (!isArbitrated(field)) continue;
    if (!m) { m = new Map(); holds.set(id, m); }
    m.set(canon(field), { until: now + holdMs, source });
  }
}

/** Release the hold on these fields — call when a confirmed DDB write-back lands
 *  (the echo of our own write: canonical has caught up, so let it reassert). */
export function releaseHolds(id: string, fields: Iterable<string>): void {
  const m = holds.get(id);
  if (!m) return;
  for (const field of fields) m.delete(canon(field));
}

/** Would an incoming change to (id, field) from `source` WIN over what we hold?
 *
 *  A table edit (Roll20/DMS) always applies locally — the table is where play
 *  happens. A DDB poll loses to a live table hold (whatever its timestamp — this is
 *  the skew-immune core and the NaN fix), and otherwise reasserts only when it is
 *  strictly newer by dateModified than the DDB value we last stored; a DDB event
 *  with no comparable time cannot prove it is newer, so it loses to anything stored.
 *  `at` is the DDB dateModified in ms (ignored for a table source). */
export function recencyWins(id: string, field: string, source: RecencySource, at: number, opts?: RecencyOpts): boolean {
  if (!isArbitrated(field)) return true;              // not arbitrated → never blocked
  if (isTableSource(source)) return true;             // the table edit wins and takes the hold (caller stamps + holds)
  // source === "ddb": canonical, subject to the hold.
  const now = opts?.now ?? Date.now();
  if (activeHold(id, field, now)) return false;       // a live table hold beats the poll, timestamp or not
  const stored = stamps.get(id)?.get(canon(field));
  if (!stored) return true;                           // never seen this field → apply
  if (!Number.isFinite(at)) return false;             // a no-time DDB event cannot prove it is newer → lose (the NaN fix)
  return at > stored.at;                              // DDB reasserts only when strictly newer than what we hold
}

/** Record that `source` changed these fields at `at`. Tracked fields only; a stamp
 *  is only advanced, never moved backwards, so a late older echo cannot rewrite a
 *  newer stamp. A table edit records its stamp with no comparable time (it uses the
 *  hold, not the clock); its stamp carries the local `at` when the caller passes one
 *  so DDB-reassert can tell "newer than the table's last edit" after the hold. */
export function noteRecency(id: string, fields: Iterable<string>, source: RecencySource, at: number): void {
  if (!Number.isFinite(at)) return;
  let m = stamps.get(id);
  for (const field of fields) {
    if (!isArbitrated(field)) continue;
    if (!m) { m = new Map(); stamps.set(id, m); }
    const prev = m.get(canon(field));
    if (!prev || at >= prev.at) m.set(canon(field), { at, source });
  }
}

/** Split an incoming change into the fields that win (apply) and those a newer /
 *  holding source already owns (drop). Untracked fields always apply. The HP triad
 *  is decided as one: because hp, maxHp and tempHp share a canonical key, one verdict
 *  governs all three present fields, so the hp ≤ maxHp invariant can never be split. */
export function gateByRecency<T extends Record<string, unknown>>(
  id: string, change: T, source: RecencySource, at: number, opts?: RecencyOpts,
): { apply: Partial<T>; dropped: string[] } {
  const apply: Partial<T> = {};
  const dropped: string[] = [];
  for (const k of Object.keys(change) as (keyof T & string)[]) {
    if (!isArbitrated(k) || recencyWins(id, k, source, at, opts)) apply[k] = change[k];
    else dropped.push(k);
  }
  return { apply, dropped };
}

/** Forget a character (left the roster / campaign switch) — stamps and holds both. */
export function forgetRecency(id: string): void { stamps.delete(id); holds.delete(id); }

/** Test/diagnostic: the current stamp for a field (triad folds to hp), or undefined. */
export function peekRecency(id: string, field: string): Readonly<Stamp> | undefined { return stamps.get(id)?.get(canon(field)); }

/** Test only: wipe all stamps and holds. */
export function __resetRecency(): void { stamps.clear(); holds.clear(); }
